import {
  PaymentMethodType,
  PaymentType,
  Task,
  TaskType,
} from '@admiin-com/ds-graphql';
import React, { useMemo } from 'react';
import PaymentInstallmentModal from '../../pages/PaymentInstallmentModal/PaymentInstallmentModal';
import { usePaymentContextDetail } from '../PaymentContainer/PaymentContainer';
import PaymentScheduleModal from '../../pages/PaymentScheduleModal/PaymentScheduleModal';
import {
  ISelectOption,
  PaymentDetailSelector,
  PaymentDetailType,
} from '../PaymentDetailSelector/PaymentDetailSelector';
import { useTranslation } from 'react-i18next';
import { useTaskProperty } from '../../hooks/useTaskProperty/useTaskProperty';
import AddPaymentMethodModal from '../AddPaymentMethodModal/AddPaymentMethodModal';
import { WBS3Image as S3Image } from '@admiin-com/ds-amplify-web';
import { gql, useQuery } from '@apollo/client';
import { CSGetSub as GET_SUB } from '@admiin-com/ds-graphql';
import { getUser } from '@admiin-com/ds-graphql';
import { getTomorrowDate } from '../../helpers/datetime';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';

export interface PaymentDetailProps {
  task?: Task | null;
  type: PaymentDetailType;
  bulkData?: Task[];
  children: React.ReactNode;
}

export function PaymentDetail({
  task,
  type,
  bulkData,
  children,
}: PaymentDetailProps) {
  const { t } = useTranslation();
  const {
    updatePayment,
    paymentDetail,
    paymentMethods,
    paymentMethod,
    setPaymentMethod,
  } = usePaymentContextDetail(task ?? null);
  // const { setSelectedSignatureKey } = useTaskBoxContext();

  const [showScheduleModal, setShowScheduleModal] =
    React.useState<boolean>(false);
  const [showInstallmentModal, setShowInstallmentModal] =
    React.useState<boolean>(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] =
    React.useState<boolean>(false);
  const [showAddSignatureModal, setShowAddSignatureModal] =
    React.useState<boolean>(false);

  const { isInstallments } = useTaskProperty(task);

  const [modalType, setModalType] = React.useState<
    'CC' | 'PAY_TO_VERIFY' | 'BankAccount' | 'ConnectedAccount'
  >('CC');

  const { data: subData } = useQuery(gql(GET_SUB));
  const userId = subData?.sub;
  const { data: userData } = useQuery(gql(getUser), {
    variables: {
      id: userId,
    },
    skip: !userId,
  });

  const user = useMemo(() => userData?.getUser || {}, [userData]);

  const [options, setOptions] = React.useState<ISelectOption[]>([]);
  const [value, setValue] = React.useState<any>('');

  const updatePaymentDetails = React.useCallback(
    (updatedData: any) => {
      if (task) {
        updatePayment({ task, ...updatedData });
      }
      if (bulkData) {
        bulkData.forEach((task) => {
          if (task) updatePayment({ task, ...updatedData });
        });
      }
    },
    [task, bulkData, updatePayment]
  );

  const onChange = (e: React.ChangeEvent<{ value: any }>) => {
    const value = e.target.value;

    let updatedData = {};
    switch (type) {
      case 'Type':
        if (value === PaymentType.SCHEDULED) {
          setShowScheduleModal(true);
        } else if (value === PaymentType.INSTALLMENTS) {
          setShowInstallmentModal(true);
        } else if (value === PaymentType.PAY_NOW) {
          updatedData = {
            installments: task?.numberOfPayments ?? 1,
            type: value,
            scheduledAt: new Date(),
          };
          setValue(value);
          updatePaymentDetails(updatedData);
        }
        break;
      case 'Method':
        console.log('valuevaluevaluevalue: ', value);
        if (value === 'newCard') {
          setModalType('CC');
          setShowPaymentMethodModal(true);
        } else if (value === 'newBank') {
          setShowPaymentMethodModal(true);
          setModalType('BankAccount');
          // setPaymentMethod(value);
        } else if (value === 'connectedAccount') {
          setShowPaymentMethodModal(true);
          setModalType('ConnectedAccount');
          console.log('connectedAccount value: ', value);
          setPaymentMethod(value);
        } else {
          setPaymentMethod(value);
        }
        break;
      case 'Signature':
        if (value === 'newSignature') {
          setShowAddSignatureModal(true);
        } else {
          setValue(value);
          // setSelectedSignatureKey(value);
        }
        // TODO: Signature change handling
        break;
    }
  };

  const taskProperty = useTaskProperty(task);

  React.useEffect(() => {
    if (type === 'Type') {
      if (paymentDetail && paymentDetail.type) {
        setValue(paymentDetail.type);
      } else
        setValue(
          taskProperty?.isInstallments
            ? PaymentType.INSTALLMENTS
            : taskProperty?.isScheduled
            ? PaymentType.SCHEDULED
            : PaymentType.PAY_NOW
        );
    }
  }, [
    type,
    paymentDetail,
    taskProperty?.isInstallments,
    taskProperty?.isScheduled,
  ]);

  React.useEffect(() => {
    if (type === 'Method' && paymentMethod) {
      setValue(paymentMethod);
    } else if (type === 'Method' && !paymentMethod) {
      setValue('newCard');
    }
  }, [paymentMethod, task, type, updatePayment]);

  React.useEffect(() => {
    const signatureKey =
      user?.selectedSignatureKey ?? user.signatures?.items[0]?.key;
    if (type === 'Signature' && signatureKey) {
      setValue(signatureKey);
      // setSelectedSignatureKey(signatureKey);
    } else if (type === 'Signature' && !signatureKey) {
      setValue('newSignature');
    }
  }, [user, type]);

  React.useEffect(() => {
    let newOptions: ISelectOption[] = [];
    switch (type) {
      case 'Signature':
        //@ts-ignore - supposed to be string
        newOptions = user?.signatures?.items.map((item) => ({
          label: (
            <S3Image
              imgKey={item?.key}
              level={'private'}
              responsive={false}
              sx={{ maxWidth: '40px' }}
            />
          ),
          value: item?.key,
          icon: (
            <S3Image
              imgKey={item?.key}
              level={'private'}
              sx={{ maxWidth: '40px' }}
            />
          ),
        }));
        break;
      case 'Type':
        newOptions = Object.values(PaymentType).map((type) => ({
          value: type,
          label: t(
            type === PaymentType.INSTALLMENTS &&
              (paymentDetail?.installments ?? taskProperty.totalInstallments) >
                1
              ? 'installmentsCount'
              : type === PaymentType.SCHEDULED
              ? 'SCHEDULE'
              : type,
            {
              ns: 'taskbox',
              installments:
                paymentDetail?.installments ?? taskProperty.totalInstallments,
            }
          ),
          disabled:
            (task && !task?.paymentTypes.includes(type)) ||
            (type === PaymentType.SCHEDULED && isInstallments && task) ||
            (task &&
              task.type === TaskType.SIGN_ONLY &&
              type === PaymentType.PAY_NOW),
        }));

        break;
      case 'Method':
        newOptions = paymentMethods;
        break;
    }
    setOptions(newOptions);
  }, [
    t,
    type,
    paymentMethods,
    paymentDetail,
    task,
    taskProperty?.totalInstallments,
    user?.signatures?.items,
    isInstallments,
  ]);

  return (
    <>
      {React.Children.map(children, (child) => {
        // Ensure child is a valid React element before cloning
        if (React.isValidElement(child)) {
          return React.cloneElement<any>(child, {
            options,
            type,
            value,
            onChange,
          });
        }
        return child;
      })}

      <AddPaymentMethodModal
        open={showPaymentMethodModal}
        onSuccess={(el: any) => {
          // if (modalType === 'CC') {
          console.log('paymentMethod details : ', el, modalType);
          if (modalType === 'CC') {
            console.log('paymentMethod details : ', el, modalType);
          }
          if (modalType === 'BankAccount') {
            setPaymentMethod(paymentMethod?.id);
          }
          if (modalType === 'ConnectedAccount') {
            setPaymentMethod(paymentMethod?.id);
          }
          // } else {
          // setBankPaymentMethod(paymentMethod);
          // setPaymentMethod(paymentMethod.id);
          // }
        }}
        type={modalType}
        handleClose={() => setShowPaymentMethodModal(false)}
        tasks={bulkData ?? (task ? [task] : [])}
      />

      <PaymentScheduleModal
        task={task}
        value={paymentDetail?.scheduledAt ?? taskProperty.scheduledAt}
        onSuccess={(date) => {
          setValue(PaymentType.SCHEDULED);
          updatePaymentDetails({
            type: PaymentType.SCHEDULED,
            scheduledAt: date,
            installments: task?.numberOfPayments ?? 1,
          });
        }}
        open={showScheduleModal}
        handleClose={() => setShowScheduleModal(false)}
      />
      <PaymentInstallmentModal
        task={task}
        paymentMethodType={
          paymentMethod === 'connectedAccount'
            ? PaymentMethodType.CONNECTED_ACCOUNT_WALLET
            : paymentMethod?.paymentMethodType || PaymentMethodType.CARD
        }
        value={paymentDetail?.installments ?? taskProperty.totalInstallments}
        isGuest={false}
        onSuccess={(value) => {
          setValue(PaymentType.INSTALLMENTS);
          updatePaymentDetails({
            type: PaymentType.INSTALLMENTS,
            installments: value,
            scheduledAt: getTomorrowDate(),
            paymentFrequency: 'MONTHLY',
          });
        }}
        open={showInstallmentModal}
        handleClose={() => setShowInstallmentModal(false)}
      />
      <AddSignatureModal
        open={showAddSignatureModal}
        handleClose={() => setShowAddSignatureModal(false)}
      />
    </>
  );
}
PaymentDetail.Selector = PaymentDetailSelector;
export default PaymentDetail;
